import React, { useState, useEffect } from "react";
import { 
  getScrappedCasingZones, 
  getScrappedCasingRegions, 
  getScrappedCasingFleetsHubs,
  getScrapCasingTyresDataByZone,
  getScrapCasingTyresDataByRegion,
  getScrapCasingTyresDataByHub,
  downloadScrapCasingTyresByZone,
  downloadScrapCasingTyresByRegion,
  downloadScrapCasingTyresByHub,
} from "../../apis/scrappedCasingTyres.service";
import { CustomBarChart } from "../../common/CustomBarChart";
import CustomDetailModal from "./CustomDetailModal";
import { BiCloudDownload } from "react-icons/bi";
import toast from "react-hot-toast";
import { exportScrapCasingTyresExcel, normalizeScrapCasingTyreRow } from "../../utils/exportExcel";
import { SCRAPPED_CASING_DETAIL_MODAL_COLUMNS } from "../../utils/scrapConstants";

const styles = {
  container: "w-full max-w-[1400px]",
  graphWrapper: "w-full relative group",
  paginationWrapper: "flex items-center justify-center gap-4 mt-6",
  pageBtn: "w-10 h-10 flex items-center justify-center border border-gray-200 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white hover:shadow-md transition-all bg-gray-50/30",
  pageInfo: "bg-white border border-gray-200 px-5 py-2 rounded-xl text-sm font-bold text-gray-400 shadow-sm",
  icon: "text-gray-500 font-bold"
};

const ScrappedCasingTyresGraph = () => {
  const [viewModeScrap, setViewModeScrap] = useState("zone");
  const [zones, setZones] = useState([]);
  const [regions, setRegions] = useState([]);
  const [fleets, setFleets] = useState([]);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    const fetchZones = async () => {
      try {
        const response = await getScrappedCasingZones();
        setZones(response.data || response);
      } catch (error) {
        console.error("Error fetching zones:", error);
      }
    };
    fetchZones();
  }, []);

  useEffect(() => {
    if (selectedDetail) {
      const fetchDetailData = async () => {
        setModalLoading(true);
        setModalData([]);
        try {
          let response;
          if (viewModeScrap === "zone") {
            response = await getScrapCasingTyresDataByZone({
              zoneName: selectedDetail.resource,
              status: selectedDetail.type
            });
          } else if (viewModeScrap === "region") {
            response = await getScrapCasingTyresDataByRegion({
              regionCode: selectedDetail.resourceId,
              status: selectedDetail.type
            });
          } else {
            response = await getScrapCasingTyresDataByHub({
              hubId: selectedDetail.resourceId,
              status: selectedDetail.type
            });
          }
          if (response?.success && response?.data) {
            setModalData(response.data);
          }
        } catch (error) {
          console.error("Error fetching modal data:", error);
        } finally {
          setModalLoading(false);
        }
      };
      fetchDetailData();
    } else {
      setModalData([]);
    }
  }, [selectedDetail, viewModeScrap]);

  const resolveDownloadTyreRows = (response) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (Array.isArray(response.data)) return response.data;
    return [];
  };

  const handleDownloadTyresData = async () => {
    if (!selectedDetail) return;
    setDownloadLoading(true);
    try {
      const status = selectedDetail.type;
      let response;
      if (viewModeScrap === "zone") {
        response = await downloadScrapCasingTyresByZone({
          zoneName: selectedDetail.resource,
          status,
        });
      } else if (viewModeScrap === "region") {
        response = await downloadScrapCasingTyresByRegion({
          regionCode: selectedDetail.resourceId,
          status,
        });
      } else {
        response = await downloadScrapCasingTyresByHub({
          hubId: selectedDetail.resourceId,
          status,
        });
      }
      const list = resolveDownloadTyreRows(response);
      if (!list.length) {
        toast.error("No data to download");
        return;
      }
      const safeTitle = `${selectedDetail.resource}-${selectedDetail.type}`.replace(/\s+/g, "_");
      exportScrapCasingTyresExcel(list, `scrap-casing-tyres_${safeTitle}`, { tyreStatus: status });
      toast.success("File downloaded");
    } catch (error) {
      console.error("Download tyres failed:", error);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleZoneClick = async (zoneName) => {
    try {
      const response = await getScrappedCasingRegions(zoneName);
      setRegions(response.data || response);
      setViewModeScrap("region");
    } catch (error) {
      console.error("Error fetching regions:", error);
    }
  };

  const handleRegionClick = async (regionName) => {
    try {
      const response = await getScrappedCasingFleetsHubs(regionName);
      const fleetData = response.data || response;
      if (Array.isArray(fleetData) && fleetData.length > 0 && fleetData[0].hubs) {
        setFleets(fleetData[0].hubs);
      } else {
        setFleets(fleetData);
      }
      setViewModeScrap("fleet");
    } catch (error) {
      console.error("Error fetching fleets:", error);
    }
  };

  const getDynamicScale = (dataValues) => {
    const highestValue = Math.max(...dataValues, 0);
    let yAxisMax, yAxisStepSize, yAxisMin = 0;

    if (highestValue >= 25000) {
      yAxisStepSize = 2000;
      yAxisMax = Math.ceil(highestValue / 2000) * 2000 + 2000;
    } else if (highestValue >= 10000) {
      yAxisStepSize = 1000;
      yAxisMax = Math.ceil(highestValue / 1000) * 1000 + 1000;
    } else if (highestValue <= 500) {
      yAxisStepSize = 50;
      yAxisMax = Math.ceil(highestValue / 50) * 50 + 50;
    } else if (highestValue <= 1000) {
      yAxisStepSize = 100;
      yAxisMax = Math.ceil(highestValue / 100) * 100 + 100;
    } else {
      yAxisStepSize = 500;
      yAxisMax = Math.ceil(highestValue / 500) * 500 + 500;
    }

    return { yAxisMin, yAxisMax, yAxisStepSize };
  };

  const getScrapChartData = () => {
    let ObjectData, xAxisTitle, activeCrumb, nextMode, rawDataList;

    if (viewModeScrap === "zone") {
      rawDataList = zones;
      ObjectData = {
        labels: zones.map((d) => d.zoneName?.split(" ") || []),
        srpData: zones.map((d) => d.totalScrap),
        cs1Data: zones.map((d) => d.totalCasing),
      };
      xAxisTitle = "Zones";
      activeCrumb = 0;
      nextMode = "region";
    } else if (viewModeScrap === "region") {
      rawDataList = regions;
      ObjectData = {
        labels: regions.map((d) => [d.regionName] || []),
        srpData: regions.map((d) => d.totalScrap),
        cs1Data: regions.map((d) => d.totalCasing),
      };
      xAxisTitle = "Regions";
      activeCrumb = 1;
      nextMode = "fleet";
    } else {
      rawDataList = fleets;
      ObjectData = {
        labels: fleets.map((d) => {
          const name = d.hubName || d.fleetName;
          if (!name) return [];
          const parts = name.split(" ");
          if (d.hubCode) parts.push(`(${d.hubCode})`);
          return parts;
        }),
        srpData: fleets.map((d) => d.scrapCount || d.totalScrap || 0),
        cs1Data: fleets.map((d) => d.casingCount || d.totalCasing || 0),
      };
      xAxisTitle = "Fleets & Hubs";
      activeCrumb = 2;
      nextMode = null;
    }

    const { yAxisMin, yAxisMax, yAxisStepSize } = getDynamicScale([...ObjectData.srpData, ...ObjectData.cs1Data]);

    const breadcrumbs = [
      {
        label: "Zone Wise",
        onClick: activeCrumb > 0 ? () => { setViewModeScrap("zone"); } : undefined,
        active: activeCrumb === 0,
      },
      ...(activeCrumb >= 1
        ? [
            {
              label: "Region Wise",
              onClick:
                activeCrumb > 1 ? () => { setViewModeScrap("region"); } : undefined,
              active: activeCrumb === 1,
            },
          ]
        : []),
      ...(activeCrumb >= 2
        ? [{ label: "Fleet & Hub Wise", active: true }]
        : []),
    ];

    const datasets = [
      { label: "SRP", data: ObjectData.srpData, backgroundColor: "#E9DFFA" },
      { label: "CS1", data: ObjectData.cs1Data, backgroundColor: "#6928DB" },
    ];

    return {
      labels: ObjectData.labels,
      datasets,
      legendItems: [
        { label: "CS1", color: "#6928DB" },
        { label: "SRP", color: "#E9DFFA" },
      ],
      xAxisTitle,
      breadcrumbs,
      onXAxisCategoryClick: nextMode
        ? async (event, elements) => {
            if (!elements?.length) return;
            const localIndex = elements[0]?.index;
            if (
              localIndex == null ||
              localIndex < 0 ||
              localIndex >= rawDataList.length
            ) {
              return;
            }

            const item = rawDataList[localIndex];
            if (!item) return;

            if (viewModeScrap === "zone") {
              const zoneName = item.zoneName ?? item.name;
              if (!zoneName) return;
              await handleZoneClick(zoneName);
            } else if (viewModeScrap === "region") {
              const regionName = item.regionName ?? item.regionCode;
              if (!regionName) return;
              await handleRegionClick(regionName);
            }
          }
        : null,
      onBarClick: (event, elements) => {
        if (!elements?.length) return;
        const localIndex = elements[0]?.index;
        const datasetIndex = elements[0]?.datasetIndex;
        const item = rawDataList[localIndex];
        const datasetLabel = datasets[datasetIndex]?.label === "CS1" ? "Casing" : "Scrap";
        
        let labelPrefix = "";
        let resourceId = "";
        if (viewModeScrap === "zone") {
          labelPrefix = item.zoneName || item.name;
          resourceId = labelPrefix;
        } else if (viewModeScrap === "region") {
          labelPrefix = item.regionName || item.regionCode;
          resourceId = item.regionCode || labelPrefix;
        } else {
          labelPrefix = `${item.hubName || item.fleetName}${item.hubCode ? ` (${item.hubCode})` : ""}`;
          resourceId = item._id || item.hubId || labelPrefix;
        }

        const value = datasets[datasetIndex]?.data[localIndex];

        setSelectedDetail({
          title: `${labelPrefix} - ${datasetLabel}`,
          resource: labelPrefix,
          resourceId: resourceId,
          type: datasetLabel,
          value: value
        });
      },
      hoverCursor: !!nextMode,
      yAxisMin,
      yAxisMax,
      yAxisStepSize,
    };
  };

  const scrapConfig = getScrapChartData();

  return (
    <div className={styles.container}>
      <div className={styles.graphWrapper}>
        <CustomBarChart
          title="Scrap & Casing Tyres Available"
          titleTextSize="19px"
          titleStyle={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            lineHeight: "100%",
            letterSpacing: "0%",
            color: "#22C55E",
          }}
          boxRadius="32px"
          boxPadding={6}
          boxBorder="1px solid #E9DFFA"
          breadcrumbs={scrapConfig.breadcrumbs}
          labels={scrapConfig.labels}
          datasets={scrapConfig.datasets}
          legendItems={scrapConfig.legendItems}
          xAxisTitle={scrapConfig.xAxisTitle}
          onXAxisCategoryClick={scrapConfig.onXAxisCategoryClick}
          onBarClick={scrapConfig.onBarClick}
          hoverCursor={scrapConfig.hoverCursor}
          stacked={false}
          barThickness={40}
          barPercentage={0.75}
          categoryPercentage={0.7}
          enableHorizontalScroll={true}
          minCategoryWidth={105}
          maxVisibleCategories={30}
          yAxisMin={scrapConfig.yAxisMin}
          yAxisMax={scrapConfig.yAxisMax}
          yAxisTitle="Number of tyres"
          yAxisTitlePosition="-top-[9px]"
          yAxisStepSize={scrapConfig.yAxisStepSize}
          interactiveTooltip={true}
          allowBarClick={false}
        />
      </div>

      <CustomDetailModal
        isOpen={!!selectedDetail}
        onClose={() => setSelectedDetail(null)}
        title={selectedDetail?.title}
        modalWidth="85vw"
        loading={modalLoading}
        filterComponent={
          <button 
            type="button"
            disabled={downloadLoading || modalLoading || !selectedDetail}
            className="flex items-center gap-2 bg-[#22C55E] text-white px-5 py-2.5 rounded-xl hover:bg-green-600 transition-all shadow-sm hover:shadow-md font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleDownloadTyresData}
          >
            <BiCloudDownload size={20} className={downloadLoading ? "animate-pulse" : ""} />
            <span>{downloadLoading ? "Preparing…" : "Download Data"}</span>
          </button>
        }
        columns={SCRAPPED_CASING_DETAIL_MODAL_COLUMNS}
        data={modalData.map((d, idx) => {
          const n = normalizeScrapCasingTyreRow(d);
          const lastInsp = n['lastInspectionDate'];
          return {
            id: idx,
            Zone: n.Zone || "-",
            Region: n.Region || "-",
            "Hub Name": n["Hub Name"] || "-",
            stencilNo: n["Stencil No"] || "-",
            productType: d.productType ?? n["Tyre Type"] ?? "-",
            LastInspectionDate: d.lastInspectionDate
              ? new Date(d.lastInspectionDate).toLocaleDateString("en-GB")
              : "-",
            DueDays:
              d["DueDays"] !== undefined && d["DueDays"] !== null && d["DueDays"] !== ""
                ? d["DueDays"]
                : "-",
          };
        })}
      />
    </div>
  );
};

export default ScrappedCasingTyresGraph;
